import React from 'react';
import { IconButton } from './IconButton.tsx';
import { cn } from '../../utils/cn.ts';

interface UndoRedoButtonsProps {
    /** Undo callback */
    onUndo: () => void;
    /** Redo callback */
    onRedo: () => void;
    /** Whether undo is available */
    canUndo: boolean;
    /** Whether redo is available */
    canRedo: boolean;
    /** Additional className for the container */
    className?: string;
}

/**
 * Undo/Redo Buttons - Shared component for tool-scoped history
 * 
 * Displays undo and redo buttons in a grouped pill style.
 * Used in Tool Action Bars for per-tool temporal state.
 * 
 * Usage:
 * ```tsx
 * <UndoRedoButtons
 *   onUndo={handleUndo}
 *   onRedo={handleRedo}
 *   canUndo={history.length > 0}
 *   canRedo={future.length > 0}
 * />
 * ```
 */
export const UndoRedoButtons: React.FC<UndoRedoButtonsProps> = ({
    onUndo,
    onRedo,
    canUndo,
    canRedo,
    className = ''
}) => (
        <div className={cn(
            'flex items-center gap-1',
            'bg-gray-100 dark:bg-gray-800',
            'rounded-lg px-1 py-1',
            'border border-gray-200 dark:border-gray-700',
            className
        )}>
            <IconButton
                onClick={onUndo}
                disabled={!canUndo}
                icon="undo"
                title="Undo (Ctrl+Z)"
                className="hover:bg-white dark:hover:bg-gray-700"
            />
            <IconButton
                onClick={onRedo}
                disabled={!canRedo}
                icon="redo"
                title="Redo (Ctrl+Y)"
                className="hover:bg-white dark:hover:bg-gray-700"
            />
        </div>
    );














